import React, { useEffect, useState } from 'react';
import { hashPassword, loadAuthConfig, saveAuthConfig, loadUsers, saveUsers } from '../utils/auth';

export default function SettingsModal({ open, onClose, settings, onChange, onImportApps, onExportApps, onResetApps }) {
  const [local, setLocal] = useState(settings || {});
  const [wallpaperPreview, setWallpaperPreview] = useState(null);

  // load wallpaper from localStorage if present
  useEffect(() => {
    try {
      const w = localStorage.getItem('my-dashboard-wallpaper');
      if (w) setWallpaperPreview(w);
    } catch (e) {}
  }, []);

  useEffect(() => setLocal(settings || {}), [settings, open]);

  // auth config loaded separately (protect dashboard)
  const [authCfg, setAuthCfg] = useState(loadAuthConfig());
  const [newPassword, setNewPassword] = useState('');

  // User management
  const [users, setUsers] = useState(loadUsers());
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);

  if (!open) return null;

  function save() {
    // Persist wallpaper choice if set (override or preset) or reset if cleared
    try {
      if (local.wallpaperOverride) {
        applyWallpaper(local.wallpaperOverride);
      } else if (local.wallpaper) {
        applyWallpaper(local.wallpaper);
      } else if (local.wallpaper === null) {
        resetWallpaper();
      }
    } catch (e) {
      console.warn('Failed to persist wallpaper on save', e);
    }

    onChange(local);
    onClose();
  }

  async function setPassword() {
    if (!newPassword) return alert('Entrez un mot de passe');
    const h = await hashPassword(newPassword);
    const cfg = { enabled: true, hash: h };
    saveAuthConfig(cfg);
    setAuthCfg(cfg);
    setNewPassword('');
    alert('Mot de passe enregistré');
  }

  function disableProtection() {
    if (!window.confirm('Désactiver la protection ?')) return;
    const cfg = { enabled: false };
    saveAuthConfig(cfg);
    setAuthCfg(cfg);
    alert('Protection désactivée');
  }

  async function addUser() {
    if (!newUsername.trim()) return alert('Entrez un nom d\'utilisateur');
    if (!newUserPassword) return alert('Entrez un mot de passe');
    if (users.find(u => u.username === newUsername.trim())) {
      return alert('Cet utilisateur existe déjà');
    }
    const hash = await hashPassword(newUserPassword);
    const updatedUsers = [...users, { 
      username: newUsername.trim(), 
      hash,
      role: 'user',
      rights: ['viewSites'] // Guest-like rights by default
    }];
    saveUsers(updatedUsers);
    setUsers(updatedUsers);
    setNewUsername('');
    setNewUserPassword('');
    alert('Utilisateur ajouté');
  }

  function removeUser(username) {
    if (username === 'ymnode' || username === 'Invité') {
      return alert('Cet utilisateur ne peut pas être supprimé');
    }
    if (!window.confirm(`Supprimer l'utilisateur "${username}" ?`)) return;
    const updatedUsers = users.filter(u => u.username !== username);
    saveUsers(updatedUsers);
    setUsers(updatedUsers);
    alert('Utilisateur supprimé');
  }

  function toggleUserRight(username, right) {
    const updatedUsers = users.map(u => {
      if (u.username === username) {
        const rights = u.rights || [];
        if (rights.includes(right)) {
          return { ...u, rights: rights.filter(r => r !== right) };
        } else {
          return { ...u, rights: [...rights, right] };
        }
      }
      return u;
    });
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
  }

  function applyWallpaper(urlOrData) {
    try {
      if (urlOrData) {
        const val = `url('${urlOrData}')`;
        document.documentElement.style.setProperty('--wallpaper-url', val);
        localStorage.setItem('my-dashboard-wallpaper', urlOrData);
        setWallpaperPreview(urlOrData);
      }
    } catch (e) {
      console.warn('Failed to apply wallpaper', e);
    }
  }

  function resetWallpaper() {
    localStorage.removeItem('my-dashboard-wallpaper');
    document.documentElement.style.setProperty('--wallpaper-url', "url('/wallpaper-linux.jpg')");
    setWallpaperPreview(null);
  }

  function handleFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result;
      applyWallpaper(data);
    };
    reader.readAsDataURL(f);
  }

  function handleImport() {
    const text = prompt('Coller le JSON des applications pour importer:');
    if (!text) return;
    try {
      const arr = JSON.parse(text);
      onImportApps(arr);
      alert('Import réussi');
    } catch (e) {
      alert('JSON invalide');
    }
  }

  // Wallpaper presets (free Unsplash small images) - you can replace these URLs
  const presets = [
    'https://images.unsplash.com/photo-1509228468518-180dd4864904?q=80&w=1400&auto=format&fit=crop&ixlib=rb-4.0.3&s=7c3a6a6d0f4b6bb4c4f8c3a9e9b9b6f1',
    'https://images.unsplash.com/photo-1502082553048-f009c37129b9?q=80&w=1400&auto=format&fit=crop&ixlib=rb-4.0.3&s=3c4a8f62a1a6d8b5b5f3e6a9b9c1a2d3',
    'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=1400&auto=format&fit=crop&ixlib=rb-4.0.3&s=8b9f5d6a1f2b3c4d5e6f7a8b9c0d1e2f'
  ];

  function choosePreset(url) {
    setLocal({ ...local, wallpaper: url, wallpaperOverride: null });
  }

  function handleFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setLocal({ ...local, wallpaperOverride: dataUrl });
      setWallpaperPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  function clearWallpaper() {
    setLocal({ ...local, wallpaper: null, wallpaperOverride: null });
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal" onMouseDown={e => e.stopPropagation()}>
        <h3>Paramètres</h3>

        <label>Prénom (affiché en bas)</label>
        <input value={local.name || ''} onChange={e => setLocal({...local, name: e.target.value})} placeholder="Votre prénom" />

        <label>Thème</label>
        <select value={local.theme || 'icloud'} onChange={e => setLocal({...local, theme: e.target.value})}>
          <option value="icloud">Mode (clair)</option>
          <option value="linux">Linux (GNOME-like)</option>
          <option value="dark">Mode Sombre</option>
        </select>

        <label>Densité</label>
        <select value={local.density || 'comfortable'} onChange={e => setLocal({...local, density: e.target.value})}>
          <option value="comfortable">Confortable</option>
          <option value="compact">Compact</option>
        </select>

        <label>
          <input type="checkbox" checked={local.showIcons ?? true} onChange={e => setLocal({...local, showIcons: e.target.checked})} /> Afficher les icônes
        </label>

        <label style={{display:'block',marginTop:8}}>Proxy Base (optionnel)</label>
        <input value={local.proxyBase || ''} placeholder="/proxy/proxy.php" onChange={e => setLocal({...local, proxyBase: e.target.value})} />

        <label style={{display:'flex',alignItems:'center',gap:8}}>
          <input type="checkbox" checked={!!local.forceLogin} onChange={e => setLocal({...local, forceLogin: e.target.checked})} /> Forcer la connexion au chargement
        </label>

        <hr />
        <h4>Protection / Authentification</h4>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <label style={{fontSize:13}}>Protection du dashboard (basic client-side)</label>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <input type="text" readOnly value={authCfg.enabled ? 'Activée' : 'Désactivée'} style={{width:120}} />
            <input type="password" placeholder="Nouveau mot de passe" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            <button className="btn" onClick={setPassword}>Définir</button>
            <button className="btn" onClick={disableProtection}>Désactiver</button>
          </div>
          <div style={{fontSize:12,color:'#6b7280'}}>Remarque: il s'agit d'une protection côté client (stockée localement). Pour une protection plus sécurisée, utilisez la protection serveur `.htaccess`.</div>

        </div>

        <hr />
        <h4>Gestion des Utilisateurs / Accès Admin</h4>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{fontSize:13}}>Utilisateurs et droits:</div>
          <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:8}}>
            {users.length === 0 ? (
              <div style={{fontSize:12,color:'#6b7280'}}>Aucun utilisateur défini</div>
            ) : (
              users.map((u) => (
                <div key={u.username} style={{padding:12,background:'rgba(0,0,0,0.03)',borderRadius:8,border:'1px solid rgba(0,0,0,0.05)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <div>
                      <span style={{fontWeight:600}}>{u.username}</span>
                      <span style={{fontSize:11,color:'#6b7280',marginLeft:8}}>({u.role || 'user'})</span>
                    </div>
                    <div style={{display:'flex',gap:6}}>
                      <button 
                        className="btn" 
                        onClick={() => setExpandedUser(expandedUser === u.username ? null : u.username)}
                        style={{padding:'4px 8px',fontSize:12}}
                      >
                        {expandedUser === u.username ? '▼' : '▶'} Droits
                      </button>
                      {u.username !== 'ymnode' && u.username !== 'Invité' && (
                        <button className="btn" onClick={() => removeUser(u.username)} style={{padding:'4px 8px',fontSize:12}}>Supprimer</button>
                      )}
                    </div>
                  </div>

                  {expandedUser === u.username && (
                    <div style={{display:'flex',flexDirection:'column',gap:6,marginTop:8,paddingTop:8,borderTop:'1px solid rgba(0,0,0,0.1)'}}>
                      {[
                        { id: 'viewSites', label: 'Voir les sites' },
                        { id: 'addSite', label: 'Ajouter un site' },
                        { id: 'removeSite', label: 'Supprimer un site' },
                        { id: 'editSite', label: 'Modifier un site' },
                        { id: 'manageUsers', label: 'Gérer les utilisateurs' }
                      ].map(right => (
                        <label key={right.id} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer'}}>
                          <input 
                            type="checkbox" 
                            checked={(u.rights || []).includes(right.id)}
                            onChange={() => toggleUserRight(u.username, right.id)}
                            disabled={u.username === 'ymnode' || u.username === 'Invité'}
                          />
                          {right.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:8,padding:12,background:'rgba(0,0,0,0.02)',borderRadius:6,border:'1px solid rgba(0,0,0,0.05)'}}>
            <label style={{fontSize:13,fontWeight:500}}>Ajouter un nouvel utilisateur:</label>
            <input 
              type="text" 
              placeholder="Nom d'utilisateur" 
              value={newUsername} 
              onChange={e => setNewUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addUser()}
            />
            <input 
              type="password" 
              placeholder="Mot de passe" 
              value={newUserPassword} 
              onChange={e => setNewUserPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addUser()}
            />
            <button className="btn primary" onClick={addUser}>Ajouter l'utilisateur</button>
            <div style={{fontSize:11,color:'#6b7280'}}>💡 Les nouveaux utilisateurs reçoivent par défaut le droit "Voir les sites" (comme invité)</div>
          </div>
        </div>

        <hr />
        <h4>Fond d'écran</h4>
        <div style={{display:'flex',gap:12,alignItems:'center'}}>
          <div style={{width:96,height:64,overflow:'hidden',borderRadius:8,border:'1px solid rgba(0,0,0,0.06)'}}>
            <img src={wallpaperPreview || '/wallpaper-linux.jpg'} alt="aperçu fond" style={{width:'100%',height:'100%',objectFit:'cover'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <input type="file" accept="image/*" onChange={handleFile} />
            <div style={{display:'flex',gap:8}}>
              <button className="btn" onClick={() => {
                const url = prompt('Coller l\'URL publique de l\'image:');
                if (url) applyWallpaper(url);
              }}>Utiliser une URL</button>
              <button className="btn" onClick={resetWallpaper}>Réinitialiser</button>
            </div>
          </div>
        </div>

          <div style={{marginTop:12}}>
            <label style={{display:'block',fontWeight:600,marginBottom:8}}>Fond d'écran (Wallpaper)</label>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              {presets.map((p, i) => (
                <button key={i} className="btn" style={{padding:0,borderRadius:8,overflow:'hidden'}} onClick={() => choosePreset(p)} title={`Sélectionner le preset ${i+1}`}>
                  <img src={p} alt={`preset-${i+1}`} style={{width:84,height:48,objectFit:'cover',display:'block'}} />
                </button>
              ))}
              <label className="btn" style={{display:'inline-flex',alignItems:'center',gap:8}}>
                Importer
                <input type="file" accept="image/*" onChange={handleFileChange} style={{display:'none'}} />
              </label>
              <button className="btn" onClick={clearWallpaper}>Retirer</button>
            </div>

            <div style={{marginTop:8}}>
              <div style={{fontSize:12,color:'#6b7280'}}>Aperçu:</div>
              <div style={{marginTop:6,width: '100%',height:120,borderRadius:8,overflow:'hidden',border:'1px solid rgba(0,0,0,0.06)'}}>
                <div style={{width:'100%',height:'100%',backgroundSize:'cover',backgroundPosition:'center',backgroundImage: local.wallpaperOverride ? `url(${local.wallpaperOverride})` : (local.wallpaper ? `url(${local.wallpaper})` : "url('/wallpaper-linux.jpg')")}} />
              </div>
            </div>
          </div>

          <div style={{display:'flex',gap:8,marginTop:12}}>
            <button className="btn" onClick={handleImport}>Importer Apps (JSON)</button>
            <button className="btn" onClick={onExportApps}>Exporter Sauvegarde</button>
            <button className="btn" onClick={() => { if (window.confirm('Réinitialiser toutes les apps ?')) onResetApps(); }}>Réinitialiser Apps</button>
          </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Annuler</button>
          <button className="btn primary" onClick={save}>Sauvegarder</button>
        </div>
      </div>
    </div>
  );
}
